import { ChangeEvent, useCallback, useState } from "react";

import { useMutation } from "@apollo/client";
import { Form, Input, message } from "antd";
import { clsx } from "clsx";
import { useSelector } from "react-redux";

import { RVButton, Tag, RVFormItem, RVRow, RVModalFooter, RVModal } from "@regal-voice/ui";
import { parseFormTags, tagOptions, TagsSelectFormItem } from "Components/shared/TagsSelect/TagsSelectFormItem";
import { useToggle } from "Hooks/useToggle";
import { isNewJourney } from "Pages/orchestration/Pages/journeys/JourneyEditPage/helpers";
import { renderErrorMessage } from "Services/LoggingService";
import { updateJourneyDetails } from "Services/marketing-api/journeys/mutations";
import {
    selectDetails,
    selectJourneyId,
    setDetails,
    setEditingJourneyDetails,
    addJourneyTag,
    removeJourneyTag,
} from "Services/state/journeys/JourneyStateSlice";
import { useRVDispatch } from "Services/state/Storage";
import { JourneyStatus } from "Types/Journey";

import { JourneyFormProvider } from "../../shared/Contexts/JourneyFormContext";

import styles from "./JourneyDetailsModal.module.scss";

// Re-exported for backwards compatibility with existing tests/imports
export { parseFormTags } from "Components/shared/TagsSelect/TagsSelectFormItem";

/**
 * Sanitizes a journey name by removing leading/trailing whitespace and newlines
 * @param name The journey name to sanitize
 * @returns The sanitized journey name
 */
export function sanitizeJourneyName(name: string): string {
    if (!name) {
        return "";
    }
    return name
        .replace(/^\s+/, "") // Trim whitespace from start only
        .replace(/^[\r\n]+|[\r\n]+$/g, ""); // Remove newlines from start and end
}

export const JOURNEY_NAME_CHARACTER_LIMIT = 70;
export const JOURNEY_DESCRIPTION_CHARACTER_LIMIT = 300;

type JourneyDetailsModalProps = {
    saveJourney?: (status: string, overrides: Record<string, any>) => Promise<void>;
    saving?: boolean;
};

/**
 * The Details modal is always open. You should conditionally render it when you need it, so it fully resets its state
 */
export function JourneyDetailsModal({ saveJourney, saving }: JourneyDetailsModalProps): JSX.Element {
    const dispatch = useRVDispatch();
    const [form] = Form.useForm();

    const journeyId = useSelector(selectJourneyId);
    const journeyDetails = useSelector(selectDetails);

    const [formSubmited, setFormSubmited] = useToggle(false);
    const [checkValidations, setCheckValidations] = useToggle(false);

    const [name, setName] = useState<string>();
    const [description, setDescription] = useState<string>();

    const nameLength = name?.length || journeyDetails.name?.length || 0;
    const descriptionLength = description?.length || journeyDetails.description?.length || 0;
    const journeyIsSaved = !isNewJourney(journeyId);

    const closeModal = useCallback(async () => {
        dispatch(setEditingJourneyDetails(false));
        // reset tags values if not saved
        form.setFieldValue(
            "journeyTags",
            journeyDetails.tags?.map((tag) => tag.id)
        );
    }, [journeyDetails, dispatch, form]);

    const formIsValid = useCallback(() => {
        return (
            nameLength > 0 &&
            nameLength <= JOURNEY_NAME_CHARACTER_LIMIT &&
            descriptionLength <= JOURNEY_DESCRIPTION_CHARACTER_LIMIT
        );
    }, [nameLength, descriptionLength]);
    /* Want to control the form a bit more rigidly than just easy breezy, should keep
    values in the component _only_, and not in the journey state slice, untill its saved,
    and not allow typing past x chars for each */
    const updateName = useCallback(
        (evt: ChangeEvent<HTMLInputElement>) => {
            const newName = sanitizeJourneyName(evt?.target?.value?.slice(0, JOURNEY_NAME_CHARACTER_LIMIT));
            setName(newName || "");
            form.setFieldValue("journeyName", newName);
        },
        [setName, form]
    );
    const updateDescription = useCallback(
        (evt: ChangeEvent<HTMLTextAreaElement>) => {
            const newDesc = evt?.target.value?.slice(0, JOURNEY_DESCRIPTION_CHARACTER_LIMIT);
            setDescription(newDesc || "");
            form.setFieldValue("journeyDescription", newDesc);
        },
        [setDescription, form]
    );

    const [update, { loading: updatePending }] = useMutation(updateJourneyDetails, {
        onCompleted: () => {
            message.success("Successfully updated journey details.");
        },
        onError: (error) => renderErrorMessage({ content: `Updating journey failed: ${error.message}.`, error }),
    });

    const onFinish = useCallback(
        async (data: Record<string, any>) => {
            const { journeyName, journeyDescription, journeyTags } = data;

            const sanitizedJourneyName = journeyName ? sanitizeJourneyName(journeyName).trim() : undefined;

            if (!formIsValid()) {
                setCheckValidations(true);
                await form.validateFields();
            } else {
                setFormSubmited(true);
                // Don't update a non existent journey (it'll fail)
                if (!journeyIsSaved) {
                    saveJourney?.(JourneyStatus.DRAFT, {
                        name: sanitizedJourneyName ?? "New Journey",
                        description: journeyDescription ?? "",
                    }).finally(() => setFormSubmited(false));
                } else {
                    const updatedJourney = await update({
                        variables: {
                            id: journeyId,
                            name: sanitizedJourneyName ?? journeyDetails?.name,
                            description: journeyDescription ?? "",
                            tags: parseFormTags(journeyTags),
                        },
                    }).finally(() => {
                        setFormSubmited(false);
                    });

                    const { tags } = updatedJourney?.data?.updateJourneyDetails;
                    dispatch(
                        setDetails({
                            name: sanitizedJourneyName || journeyDetails.name,
                            description: journeyDescription || journeyDetails.description,
                            tags,
                        })
                    );
                    dispatch(setEditingJourneyDetails(false));
                }
            }
        },
        [
            setCheckValidations,
            saveJourney,
            journeyDetails,
            journeyIsSaved,
            journeyId,
            formIsValid,
            update,
            dispatch,
            form,
            setFormSubmited,
        ]
    );

    const submitForm = useCallback(() => {
        form.submit();
    }, [form]);

    const validateJourneyNameNotEmpty = useCallback(async () => {
        if (checkValidations && nameLength == 0) {
            throw new Error("Journey Name Is Empty");
        }
    }, [nameLength, checkValidations]);

    const addTag = useCallback(
        (newTag: Tag) => {
            const { journeyTags: formJourneyTags } = form.getFieldsValue();
            form.setFieldValue("journeyTags", formJourneyTags.concat(newTag.id));
        },
        [form]
    );

    const onTagDeselect = useCallback(
        (tagId: string) => {
            dispatch(removeJourneyTag(tagId));
        },
        [dispatch]
    );

    const onTagSelect = useCallback(
        (tag: Tag) => {
            dispatch(addJourneyTag(tag));
        },
        [dispatch]
    );

    return (
        <RVModal
            data-testid="journey-details-modal"
            className={styles.journeyEditModal}
            title="Journey Details"
            open
            onCancel={closeModal}
            footer={
                <RVModalFooter
                    rightButtons={
                        <>
                            <RVButton onClick={closeModal}>Cancel</RVButton>
                            <RVButton
                                data-testid="save-journey-button"
                                loading={updatePending || saving || formSubmited}
                                onClick={submitForm}
                                type="primary"
                                disabled={checkValidations && !formIsValid()}
                            >
                                {journeyIsSaved ? "Submit" : "Save"}
                            </RVButton>
                        </>
                    }
                />
            }
        >
            <Form form={form} name={`journey-editor`} layout="vertical" onFinish={onFinish}>
                <JourneyFormProvider form={form} formSubmited={formSubmited}>
                    <RVFormItem
                        initialValue={journeyDetails?.name}
                        name="journeyName"
                        label="Journey Name"
                        rules={[
                            {
                                validator: validateJourneyNameNotEmpty,
                                message: "Journey Name can't be empty",
                            },
                        ]}
                        extra={
                            <RVRow
                                data-testid="name-count"
                                fullWidth
                                justifyContent="end"
                                className={clsx(styles.characterCountContainer, {
                                    [styles.isOver]: JOURNEY_NAME_CHARACTER_LIMIT <= nameLength,
                                })}
                            >
                                <span>
                                    {nameLength} / {JOURNEY_NAME_CHARACTER_LIMIT}
                                </span>
                            </RVRow>
                        }
                    >
                        <Input onChange={updateName} placeholder="Journey Name" />
                    </RVFormItem>

                    <RVFormItem
                        initialValue={journeyDetails?.description}
                        name="journeyDescription"
                        label="Description (Optional)"
                        extra={
                            <RVRow
                                data-testid="description-count"
                                justifyContent="space-between"
                                className={styles.characterCountContainer}
                                fullWidth
                            >
                                <span>
                                    {descriptionLength == JOURNEY_DESCRIPTION_CHARACTER_LIMIT &&
                                        "Max character limit reached."}
                                </span>
                                <span>
                                    {descriptionLength} / {JOURNEY_DESCRIPTION_CHARACTER_LIMIT}
                                </span>
                            </RVRow>
                        }
                    >
                        <Input.TextArea
                            style={{
                                resize: "none",
                            }}
                            onChange={updateDescription}
                            className={styles.editorTextArea}
                            placeholder="Description (Optional)"
                        />
                    </RVFormItem>

                    {!isNewJourney(journeyId) && (
                        <TagsSelectFormItem
                            fieldName="journeyTags"
                            initialTags={tagOptions(journeyDetails?.tags)}
                            addTagToEntityState={addTag}
                            onTagDeselect={onTagDeselect}
                            onTagSelect={onTagSelect}
                            size="middle"
                        />
                    )}
                </JourneyFormProvider>
            </Form>
        </RVModal>
    );
}
